import React, { useState, useContext } from "react";
import classNames from "classnames/bind";
import styles from "./CustomerCare.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faCheckCircle,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { ToastContext } from "~/components/context/ToastContext";
import { ModalContext } from "~/components/context/ModalContext";
import { BASE_CUSTOMER_CARE_URL } from "~/helper/apiService";
import { removeVietnameseTones, generateExcelFileName } from "./customerCareHelpers";

const cx = classNames.bind(styles);

function CareListPage() {
  const [loading, setLoading] = useState(false);
  const [careList, setCareList] = useState([]);
  const [minDays, setMinDays] = useState("30");
  const [minVisits, setMinVisits] = useState("2");
  const [limit, setLimit] = useState("100");
  const [totalCount, setTotalCount] = useState(0);
  const [selectedPhones, setSelectedPhones] = useState([]);
  const [careNote, setCareNote] = useState("");
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);

  const fetchCareList = async () => {
    setLoading(true);
    try {
      const minDaysValue = minDays === "" ? 30 : parseInt(minDays) || 30;
      const minVisitsValue = minVisits === "" ? 2 : parseInt(minVisits) || 2;
      const limitValue = limit === "" ? 100 : parseInt(limit) || 100;
      const url = `${BASE_CUSTOMER_CARE_URL}/care-list?minDays=${minDaysValue}&minVisits=${minVisitsValue}${limitValue ? `&limit=${limitValue}` : ''}`;
      const response = await fetch(url, {
        credentials: "include",
      });
      const data = await response.json();
      if (data.success) {
        setCareList(data.careList || []);
        setTotalCount(data.total || 0);
      } else {
        showToast(data.error || "Lỗi khi lấy danh sách", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BASE_CUSTOMER_CARE_URL}/refresh`, {
        credentials: "include",
      });
      const data = await response.json();
      if (data.success) {
        showToast("Đã refresh dữ liệu thành công", "success");
        fetchCareList();
      } else {
        showToast(data.error || "Lỗi khi refresh", "error");
      }
    } catch (error) {
      showToast("Lỗi kết nối: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedPhones.length === careList.length) {
      setSelectedPhones([]);
    } else {
      setSelectedPhones(careList.map((c) => c.phone));
    }
  };

  const handleSelectPhone = (phone) => {
    if (selectedPhones.includes(phone)) {
      setSelectedPhones(selectedPhones.filter((p) => p !== phone));
    } else {
      setSelectedPhones([...selectedPhones, phone]);
    }
  };

  const handleMarkAsCared = () => {
    if (selectedPhones.length === 0) {
      showToast("Vui lòng chọn ít nhất một khách hàng", "error");
      return;
    }

    showModal({
      title: "Xác nhận đánh dấu Đã CSKH",
      message: `Bạn có chắc chắn muốn đánh dấu ${selectedPhones.length} khách hàng là Đã CSKH?`,
      onConfirm: async () => {
        try {
          const response = await fetch(`${BASE_CUSTOMER_CARE_URL}/mark-as-cared`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              phones: selectedPhones,
              note: careNote,
            }),
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          if (data.success) {
            showToast(`Đã đánh dấu ${data.updated} khách hàng là Đã CSKH`, "success");
            const savedNote = careNote;
            setSelectedPhones([]);
            setCareNote("");
            // Tự động tải file Excel sau khi chăm sóc thành công
            try {
              const minDaysValue = minDays === "" ? 30 : parseInt(minDays) || 30;
              const minVisitsValue = minVisits === "" ? 2 : parseInt(minVisits) || 2;
              const excelResponse = await fetch(`${BASE_CUSTOMER_CARE_URL}/export-excel?minDays=${minDaysValue}&minVisits=${minVisitsValue}`, {
                credentials: "include",
              });
              if (excelResponse.ok) {
                const blob = await excelResponse.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = generateExcelFileName(savedNote);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              }
            } catch (excelError) {
              console.error("Error downloading Excel:", excelError);
            }
            handleRefresh().catch(err => console.error("Error refreshing:", err));
            fetchCareList().catch(err => console.error("Error fetching care list:", err));
          } else {
            showToast(data.error || "Lỗi khi đánh dấu", "error");
          }
        } catch (error) {
          console.error("Error marking as cared:", error);
          showToast("Lỗi kết nối: " + error.message, "error");
        }
        return true;
      },
      onCancel: null,
    });
  };

  const handleMarkAsCaredQuick = () => {
    if (selectedPhones.length === 0) {
      showToast("Vui lòng chọn ít nhất một khách hàng", "error");
      return;
    }

    showModal({
      title: "Xác nhận đánh dấu Đã CSKH",
      message: `Bạn có chắc chắn muốn đánh dấu ${selectedPhones.length} khách hàng là Đã CSKH?`,
      onConfirm: async () => {
        try {
          const response = await fetch(`${BASE_CUSTOMER_CARE_URL}/mark-as-cared`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({
              phones: selectedPhones,
              note: "",
            }),
          });
          
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          
          const data = await response.json();
          if (data.success) {
            showToast(`Đã đánh dấu ${data.updated} khách hàng là Đã CSKH`, "success");
            setSelectedPhones([]);
            // Tự động tải file Excel sau khi chăm sóc thành công
            try {
              const minDaysValue = minDays === "" ? 30 : parseInt(minDays) || 30;
              const minVisitsValue = minVisits === "" ? 2 : parseInt(minVisits) || 2;
              const excelResponse = await fetch(`${BASE_CUSTOMER_CARE_URL}/export-excel?minDays=${minDaysValue}&minVisits=${minVisitsValue}`, {
                credentials: "include",
              });
              if (excelResponse.ok) {
                const blob = await excelResponse.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = generateExcelFileName("");
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              }
            } catch (excelError) {
              console.error("Error downloading Excel:", excelError);
            }
            handleRefresh().catch(err => console.error("Error refreshing:", err));
            fetchCareList().catch(err => console.error("Error fetching care list:", err));
          } else {
            showToast(data.error || "Lỗi khi đánh dấu", "error");
          }
        } catch (error) {
          console.error("Error marking as cared:", error);
          showToast("Lỗi kết nối: " + error.message, "error");
        }
        return true;
      },
      onCancel: null,
    });
  };

  const getSeverityClass = (days) => {
    if (days >= 90) return "severity-lost";
    if (days >= 60) return "severity-cold";
    if (days >= 30) return "severity-risk";
    return "severity-urgent";
  };

  const SkeletonRow = () => (
    <tr className={cx("tr")}>
      <td className={cx("td", "checkbox-col")}>
        <div className={cx("skeleton", "skeleton-checkbox")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
      </td>
      <td className={cx("td", "severity")}>
        <div className={cx("skeleton", "skeleton-badge")}></div>
      </td>
      <td className={cx("td")}>
        <div className={cx("skeleton", "skeleton-text", "skeleton-short")}></div>
      </td>
    </tr>
  );

  const renderTable = () => {
    if (loading) {
      return (
        <table className={cx("table")}>
          <thead>
            <tr>
              <th className={cx("th", "checkbox-col")}></th>
              <th className={cx("th")}>SĐT</th>
              <th className={cx("th")}>Tổng lượt đi</th>
              <th className={cx("th")}>Ngày đi gần nhất</th>
              <th className={cx("th")}>Số ngày chưa quay lại</th>
              <th className={cx("th")}>Mức độ</th>
              <th className={cx("th")}>Trạng thái CSKH</th>
            </tr>
          </thead>
          <tbody>
            {[...Array(10)].map((_, index) => (
              <SkeletonRow key={index} />
            ))}
          </tbody>
        </table>
      );
    }

    if (careList.length === 0) {
      return <div className={cx("no-data")}>Không có dữ liệu</div>;
    }

    return (
      <>
        <table className={cx("table")}>
          <thead>
            <tr>
              <th className={cx("th", "checkbox-col")}>
                <input
                  type="checkbox"
                  checked={selectedPhones.length === careList.length && careList.length > 0}
                  onChange={handleSelectAll}
                />
              </th>
              <th className={cx("th")}>SĐT</th>
              <th className={cx("th")}>Tổng lượt đi</th>
              <th className={cx("th")}>Ngày đi gần nhất</th>
              <th className={cx("th")}>Số ngày chưa quay lại</th>
              <th className={cx("th")}>Mức độ</th>
              <th className={cx("th")}>Trạng thái CSKH</th>
            </tr>
          </thead>
          <tbody>
            {careList.map((customer, index) => (
              <tr key={index} className={cx("tr")}>
                <td className={cx("td", "checkbox-col")}>
                  <input
                    type="checkbox"
                    checked={selectedPhones.includes(customer.phone)}
                    onChange={() => handleSelectPhone(customer.phone)}
                  />
                </td>
                <td className={cx("td")}>{customer.phone}</td>
                <td className={cx("td")}>{customer.totalVisits}</td>
                <td className={cx("td")}>{customer.lastVisitDate}</td>
                <td className={cx("td")}>{customer.daysSinceLastVisit !== null ? customer.daysSinceLastVisit : "-"}</td>
                <td className={cx("td", "severity")}>
                  <span className={cx("severity-badge", getSeverityClass(customer.daysSinceLastVisit))}>
                    {customer.severityEmoji} {customer.severity}
                  </span>
                </td>
                <td className={cx("td")}>{customer.careStatus}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className={cx("mobile-card-container")}>
          {careList.map((customer, index) => (
            <div key={index} className={cx("mobile-card")}>
              <div className={cx("mobile-card-header")}>
                <div className={cx("mobile-card-title")}>{customer.phone}</div>
                <input
                  type="checkbox"
                  className={cx("mobile-card-checkbox")}
                  checked={selectedPhones.includes(customer.phone)}
                  onChange={() => handleSelectPhone(customer.phone)}
                />
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Tổng lượt đi:</span>
                <span className={cx("mobile-card-value")}>{customer.totalVisits}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Ngày đi gần nhất:</span>
                <span className={cx("mobile-card-value")}>{customer.lastVisitDate}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Số ngày chưa quay lại:</span>
                <span className={cx("mobile-card-value")}>{customer.daysSinceLastVisit !== null ? customer.daysSinceLastVisit : "-"}</span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Mức độ:</span>
                <span className={cx("mobile-card-value")}>
                  <span className={cx("severity-badge", getSeverityClass(customer.daysSinceLastVisit))}>
                    {customer.severityEmoji} {customer.severity}
                  </span>
                </span>
              </div>
              <div className={cx("mobile-card-row")}>
                <span className={cx("mobile-card-label")}>Trạng thái CSKH:</span>
                <span className={cx("mobile-card-value")}>{customer.careStatus}</span>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className={cx("customer-care")}>
      <div className={cx("header")}>
        <h2 className={cx("title")}>Danh Sách Cần Chăm Sóc</h2>
        <div className={cx("actions")}>
          <button
            className={cx("button", "refresh-button")}
            onClick={handleRefresh}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} /> Refresh
          </button>
        </div>
      </div>

      <div className={cx("tab-content")}>
        <div className={cx("filters")}>
          <div className={cx("filter-group")}>
            <label>Số ngày chưa quay lại ≥</label>
            <input
              type="text"
              value={minDays}
              onChange={(e) => setMinDays(e.target.value)}
              className={cx("filter-input")}
            />
          </div>
          <div className={cx("filter-group")}>
            <label>Tổng lượt đi ≥</label>
            <input
              type="text"
              value={minVisits}
              onChange={(e) => setMinVisits(e.target.value)}
              className={cx("filter-input")}
            />
          </div>
          <div className={cx("filter-group")}>
            <label>Số lượng hiển thị</label>
            <input
              type="text"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className={cx("filter-input")}
            />
          </div>
          <button
            className={cx("button", "search-button")}
            onClick={fetchCareList}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSearch} spin={loading} /> Bắt đầu tìm kiếm
          </button>
          {selectedPhones.length > 0 && (
            <button
              className={cx("button", "cared-button")}
              onClick={handleMarkAsCaredQuick}
            >
              <FontAwesomeIcon icon={faCheckCircle} /> Đã chăm sóc ({selectedPhones.length})
            </button>
          )}
        </div>
        {totalCount > 0 && (
          <div className={cx("count-info")}>
            Hiển thị {careList.length} / {totalCount} khách hàng
          </div>
        )}

        {selectedPhones.length > 0 && (
          <div className={cx("mark-as-cared-section")}>
            <div className={cx("selected-info")}>
              Đã chọn: {selectedPhones.length} khách hàng
            </div>
            <div className={cx("note-input-group")}>
              <label>Ghi chú (tùy chọn):</label>
              <input
                type="text"
                value={careNote}
                onChange={(e) => setCareNote(e.target.value)}
                placeholder="Nhập ghi chú..."
                className={cx("note-input")}
              />
            </div>
            <button
              className={cx("button", "mark-button")}
              onClick={handleMarkAsCared}
            >
              <FontAwesomeIcon icon={faCheckCircle} /> Đánh dấu Đã CSKH ({selectedPhones.length})
            </button>
          </div>
        )}

        <div className={cx("table-container")}>
          {renderTable()}
        </div>
      </div>
    </div>
  );
}

export default CareListPage;
